<template>
  <div ref="pdfContent" class="pdf-content">
    <CCard class="border-0">
      <CCardHeader class="no-print bg-transparent">
        <div class="d-flex justify-content-end">
          <CButton
            color="light"
            class="mr-2"
            @click="exportPDF"
            :disabled="isGeneratingPDF"
          >
            <CSpinner v-if="isGeneratingPDF" size="sm" class="mr-1" />
            <CIcon v-else name="cil-cloud-download" class="mr-1" />
            {{ isGeneratingPDF ? "Preparing PDF…" : "Download PDF" }}
          </CButton>
          <CButton color="light" @click="printContent">
            <CIcon name="cil-print" class="mr-1" /> Print
          </CButton>
        </div>
      </CCardHeader>

      <CCardBody class="p-3">
        <!-- Header Section -->
        <div class="document-header">
          <CRow class="justify-content-between align-items-start mb-2">
            <CCol>
              <img
                v-if="getImageUrl()"
                :src="getImageUrl()"
                alt="Company Logo"
                class="company-logo"
              />
            </CCol>
            <CCol class="text-right">
              <h1 class="document-title mb-1">PAYSLIP</h1>
              <h5 class="company-name mb-1">{{ payslip.business?.name }}</h5>
              <p class="company-reg mb-0">{{ payslip.business?.regNo ? `(${payslip.business.regNo})` : '' }}</p>
              <div class="company-address mb-1" v-if="payslip.business?.address" v-html="formatAddress(payslip.business.address)"></div>
              <p class="company-phone mb-0" v-if="payslip.business?.phone">Tel: {{ payslip.business.phone }}</p>
            </CCol>
          </CRow>

          <hr class="section-divider" />
        </div>

        <!-- Employee and Payroll Details -->
        <div class="document-details mb-2">
          <CRow>
            <CCol xs="12" md="6" class="mb-2 mb-md-0">
              <div class="employee-section">
                <h6 class="section-title">Employee details</h6>
                <table class="info-table mb-2">
                  <tbody>
                    <tr>
                      <td class="info-label">Name</td>
                      <td class="info-value"><strong>{{ getEmployeeName() }}</strong></td>
                    </tr>
                    <tr v-if="payslip.employeeNo || payslip.profile?.employeeNo">
                      <td class="info-label">Employee no.</td>
                      <td class="info-value">{{ payslip.employeeNo || payslip.profile?.employeeNo }}</td>
                    </tr>
                    <tr v-if="payslip.icNumber || payslip.profile?.myKad">
                      <td class="info-label">IC or passport</td>
                      <td class="info-value">{{ payslip.icNumber || payslip.profile?.myKad || payslip.profile?.passportNo }}</td>
                    </tr>
                    <tr v-if="payslip.department || payslip.profile?.department?.name">
                      <td class="info-label">Department</td>
                      <td class="info-value">{{ payslip.department || payslip.profile?.department?.name }}</td>
                    </tr>
                    <tr v-if="payslip.position || payslip.profile?.position">
                      <td class="info-label">Position</td>
                      <td class="info-value">{{ payslip.position || payslip.profile?.position }}</td>
                    </tr>
                    <tr v-if="payslip.joinDate || payslip.profile?.joinDate">
                      <td class="info-label">Join date</td>
                      <td class="info-value">{{ formatDate(payslip.joinDate || payslip.profile?.joinDate) }}</td>
                    </tr>
                    <tr v-if="payslip.epfNo || payslip.profile?.epfNo">
                      <td class="info-label">EPF no.</td>
                      <td class="info-value">{{ payslip.epfNo || payslip.profile?.epfNo }}</td>
                    </tr>
                    <tr v-if="payslip.socsoNo || payslip.profile?.socsoNo">
                      <td class="info-label">SOCSO no.</td>
                      <td class="info-value">{{ payslip.socsoNo || payslip.profile?.socsoNo }}</td>
                    </tr>
                    <tr v-if="payslip.taxNo || payslip.profile?.taxNo">
                      <td class="info-label">Tax Ref No:</td>
                      <td class="info-value">{{ payslip.taxNo || payslip.profile?.taxNo }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CCol>

            <CCol xs="12" md="6">
              <div class="payroll-info">
                <h6 class="section-title">Pay details</h6>
                <table class="info-table">
                  <tbody>
                    <tr>
                      <td class="info-label">Payslip no.</td>
                      <td class="info-value">{{ payslip.no || payslip.payrollNo }}</td>
                    </tr>
                    <tr>
                      <td class="info-label">Pay period</td>
                      <td class="info-value">{{ payslip.payPeriod || getPayPeriod() }}</td>
                    </tr>
                    <tr>
                      <td class="info-label">Date</td>
                      <td class="info-value">{{ getPayrollDate() }}</td>
                    </tr>
                    <tr v-if="payslip.paymentDate">
                      <td class="info-label">Paid on</td>
                      <td class="info-value">{{ formatDate(payslip.paymentDate) }}</td>
                    </tr>
                    <tr v-if="payslip.paymentMethod || payslip.paymentMethodDescription">
                      <td class="info-label">Payment method</td>
                      <td class="info-value">{{ payslip.paymentMethodDescription || getPaymentMethodName(payslip.paymentMethod) }}</td>
                    </tr>
                    <tr v-if="payslip.bankName">
                      <td class="info-label">Bank</td>
                      <td class="info-value">{{ payslip.bankName }}</td>
                    </tr>
                    <tr v-if="payslip.bankAccountNo">
                      <td class="info-label">Account no.</td>
                      <td class="info-value">{{ payslip.bankAccountNo }}</td>
                    </tr>
                    <tr v-if="payslip.paymentReferenceNo">
                      <td class="info-label">Payment reference</td>
                      <td class="info-value">{{ payslip.paymentReferenceNo }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </CCol>
          </CRow>
        </div>

        <!-- Earnings and Deductions Compact Layout -->
        <CRow class="mb-2">
          <CCol xs="12" md="6" class="mb-2">
            <h6 class="section-title mb-1">Earnings</h6>
            <table class="payroll-table table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Description</th>
                  <th class="text-right" style="width: 110px">Amount (RM)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in payslip.earnings" :key="item.id || index">
                  <td>{{ item.description || humanize(item.typeDescription) }}</td>
                  <td class="text-right">{{ formatCurrency(item.amount) }}</td>
                </tr>
                <!-- Fallback: Show gross amount if no earnings array -->
                <tr v-if="(!payslip.earnings || payslip.earnings.length === 0) && payslip.grossAmount > 0">
                  <td>Basic salary</td>
                  <td class="text-right">{{ formatCurrency(payslip.grossAmount) }}</td>
                </tr>
                <tr v-if="(!payslip.earnings || payslip.earnings.length === 0) && !payslip.grossAmount">
                  <td colspan="2" class="text-center text-muted">No earnings</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="table-total">
                  <td class="text-right"><strong>Gross pay</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(grossIncome) }}</strong></td>
                </tr>
              </tfoot>
            </table>
          </CCol>

          <CCol xs="12" md="6" class="mb-2">
            <h6 class="section-title mb-1">Deductions</h6>
            <table class="payroll-table table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Description</th>
                  <th class="text-right" style="width: 110px">Amount (RM)</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(item, index) in payslip.deductions" :key="item.id || index">
                  <td>{{ item.description || humanize(item.typeDescription) }}</td>
                  <td class="text-right">{{ formatCurrency(item.amount) }}</td>
                </tr>
                <!-- Statutory deductions -->
                <tr v-if="payslip.isEnableEpf && payslip.employeeEpfAmount > 0">
                  <td>EPF</td>
                  <td class="text-right">{{ formatCurrency(payslip.employeeEpfAmount) }}</td>
                </tr>
                <tr v-if="payslip.isEnableSocso && payslip.employeeSocsoAmount > 0">
                  <td>SOCSO</td>
                  <td class="text-right">{{ formatCurrency(payslip.employeeSocsoAmount) }}</td>
                </tr>
                <tr v-if="payslip.isEnableEis && payslip.employeeEisAmount > 0">
                  <td>EIS</td>
                  <td class="text-right">{{ formatCurrency(payslip.employeeEisAmount) }}</td>
                </tr>
                <tr v-if="payslip.isEnableSkbbk && payslip.employeeSkbbkAmount > 0">
                  <td>LINDUNG 24 Jam (SKBBK)</td>
                  <td class="text-right">{{ formatCurrency(payslip.employeeSkbbkAmount) }}</td>
                </tr>
                <tr v-if="payslip.isEnableTax && payslip.taxAmount > 0">
                  <td>Income tax (PCB)</td>
                  <td class="text-right">{{ formatCurrency(payslip.taxAmount) }}</td>
                </tr>
                <tr v-if="payslip.isEnableZakat && payslip.zakatAmount > 0">
                  <td>Zakat</td>
                  <td class="text-right">{{ formatCurrency(payslip.zakatAmount) }}</td>
                </tr>
                <tr v-if="(!payslip.deductions || payslip.deductions.length === 0) && !hasAnyStatutoryDeductions">
                  <td colspan="2" class="text-center text-muted">No deductions</td>
                </tr>
              </tbody>
              <tfoot>
                <tr class="table-total">
                  <td class="text-right"><strong>Total deductions</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(totalDeductions) }}</strong></td>
                </tr>
              </tfoot>
            </table>
          </CCol>
        </CRow>

        <!-- Net Pay Section -->
        <div class="netpay-section mb-2">
          <CRow>
            <CCol md="6" class="offset-md-6">
              <div class="net-pay-box p-2">
                <div class="d-flex justify-content-between align-items-center">
                  <h6 class="mb-0">Net pay</h6>
                  <h5 class="mb-0 text-success">
                    <strong>RM {{ formatCurrency(netPay) }}</strong>
                  </h5>
                </div>
              </div>
            </CCol>
          </CRow>
        </div>

        <!-- Statutory Contributions (Malaysian Compliance) -->
        <div class="statutory-section mb-2" v-if="hasStatutoryContributions">
          <h6 class="section-title mb-1">Statutory contributions (RM)</h6>
          <table class="payroll-table table table-bordered table-sm">
            <thead>
              <tr>
                <th>Type</th>
                <th class="text-right" style="width: 100px">Employee</th>
                <th class="text-right" style="width: 100px">Employer</th>
                <th class="text-right" style="width: 100px">Total</th>
              </tr>
            </thead>
            <tbody>
              <!-- EPF -->
              <tr v-if="payslip.isEnableEpf">
                <td>EPF</td>
                <td class="text-right">{{ formatCurrency(payslip.employeeEpfAmount || 0) }}</td>
                <td class="text-right">{{ formatCurrency(payslip.employerEpfAmount || 0) }}</td>
                <td class="text-right">{{ formatCurrency((payslip.employeeEpfAmount || 0) + (payslip.employerEpfAmount || 0)) }}</td>
              </tr>
              <!-- SOCSO -->
              <tr v-if="payslip.isEnableSocso">
                <td>SOCSO</td>
                <td class="text-right">{{ formatCurrency(payslip.employeeSocsoAmount || 0) }}</td>
                <td class="text-right">{{ formatCurrency(payslip.employerSocsoAmount || 0) }}</td>
                <td class="text-right">{{ formatCurrency((payslip.employeeSocsoAmount || 0) + (payslip.employerSocsoAmount || 0)) }}</td>
              </tr>
              <!-- EIS -->
              <tr v-if="payslip.isEnableEis">
                <td>EIS</td>
                <td class="text-right">{{ formatCurrency(payslip.employeeEisAmount || 0) }}</td>
                <td class="text-right">{{ formatCurrency(payslip.employerEisAmount || 0) }}</td>
                <td class="text-right">{{ formatCurrency((payslip.employeeEisAmount || 0) + (payslip.employerEisAmount || 0)) }}</td>
              </tr>
              <!-- SKBBK (LINDUNG 24 Jam) - employee-borne only -->
              <tr v-if="payslip.isEnableSkbbk">
                <td>LINDUNG 24 Jam (SKBBK)</td>
                <td class="text-right">{{ formatCurrency(payslip.employeeSkbbkAmount || 0) }}</td>
                <td class="text-right">-</td>
                <td class="text-right">{{ formatCurrency(payslip.employeeSkbbkAmount || 0) }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- YTD Summary and Leave Balance in 2 columns -->
        <CRow class="mb-2" v-if="hasYTDOrLeaveData">
          <!-- YTD Summary -->
          <CCol xs="12" md="6" class="mb-2" v-if="hasYTDData">
            <h6 class="section-title mb-1">Year to date (RM)</h6>
            <table class="payroll-table table table-bordered table-sm">
              <tbody>
                <tr>
                  <td>Gross pay</td>
                  <td class="text-right">{{ formatCurrency(payslip.ytdGrossAmount || 0) }}</td>
                </tr>
                <tr>
                  <td>EPF (Employee)</td>
                  <td class="text-right">{{ formatCurrency(payslip.ytdEpfEmployee || 0) }}</td>
                </tr>
                <tr>
                  <td>SOCSO (Employee)</td>
                  <td class="text-right">{{ formatCurrency(payslip.ytdSocsoEmployee || 0) }}</td>
                </tr>
                <tr v-if="payslip.ytdEisEmployee > 0">
                  <td>EIS (Employee)</td>
                  <td class="text-right">{{ formatCurrency(payslip.ytdEisEmployee || 0) }}</td>
                </tr>
                <tr v-if="payslip.ytdSkbbkEmployee > 0">
                  <td>LINDUNG 24 Jam (SKBBK)</td>
                  <td class="text-right">{{ formatCurrency(payslip.ytdSkbbkEmployee || 0) }}</td>
                </tr>
                <tr v-if="payslip.ytdTax > 0">
                  <td>Income tax (PCB)</td>
                  <td class="text-right">{{ formatCurrency(payslip.ytdTax || 0) }}</td>
                </tr>
                <tr class="table-total">
                  <td><strong>Net pay</strong></td>
                  <td class="text-right"><strong>{{ formatCurrency(payslip.ytdNetPay || 0) }}</strong></td>
                </tr>
              </tbody>
            </table>
          </CCol>

          <!-- Leave Balance -->
          <CCol xs="12" md="6" class="mb-2" v-if="hasLeaveBalance">
            <h6 class="section-title mb-1">Leave balance</h6>
            <table class="payroll-table table table-bordered table-sm">
              <thead>
                <tr>
                  <th>Leave</th>
                  <th class="text-right" style="width: 80px">Balance</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Annual leave</td>
                  <td class="text-right">{{ formatLeave(payslip.annualLeaveBalance) }}</td>
                </tr>
                <tr>
                  <td>Medical leave</td>
                  <td class="text-right">{{ formatLeave(payslip.medicalLeaveBalance) }}</td>
                </tr>
                <tr>
                  <td>Sick leave</td>
                  <td class="text-right">{{ formatLeave(payslip.sickLeaveBalance) }}</td>
                </tr>
              </tbody>
            </table>
          </CCol>
        </CRow>

        <!-- Remarks Section -->
        <div class="remarks-section mb-2" v-if="payslip.remarks">
          <h6 class="section-title mb-1">Remarks</h6>
          <div class="remarks-box p-2">
            <p class="remarks-text mb-0">{{ payslip.remarks }}</p>
          </div>
        </div>

        <!-- Footer -->
        <div class="document-footer text-center">
          <hr class="footer-divider">
          <div class="footer-content">
            <p class="footer-note mb-2">
              This payslip was generated by the system and does not need a signature.
            </p>
            <p class="company-info mb-1">
              {{ payslip.business?.name }} | {{ payslip.business?.website }} | {{ payslip.business?.phone }}
            </p>
            <p class="powered-by mb-0">
              Powered By
              <img src="/logo.png" alt="Niaga-ku Logo" class="footer-logo" />
              Niaga-Ku.com
            </p>
          </div>
        </div>
      </CCardBody>
    </CCard>
  </div>
</template>

<script>
import { formatDate as formatMytDate } from "@/utils/datetime";
import { formatMyr } from "@/utils/money";
import html2pdf from "html2pdf.js";

export default {
  name: "WidgetsReportPayslip",
  props: {
    payslip: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      isGeneratingPDF: false,
    };
  },
  computed: {
    grossIncome() {
      // First try earnings array
      if (this.payslip.earnings && this.payslip.earnings.length > 0) {
        return this.payslip.earnings.reduce((sum, item) => {
          return sum + (parseFloat(item.amount) || 0);
        }, 0);
      }

      // Fallback to grossAmount field
      return parseFloat(this.payslip.grossAmount) || 0;
    },
    totalDeductions() {
      let total = 0;

      // Add regular deductions array
      if (this.payslip.deductions && this.payslip.deductions.length > 0) {
        total += this.payslip.deductions.reduce((sum, item) => {
          return sum + (parseFloat(item.amount) || 0);
        }, 0);
      }

      // Add statutory employee deductions
      if (this.payslip.isEnableEpf && this.payslip.employeeEpfAmount) {
        total += parseFloat(this.payslip.employeeEpfAmount) || 0;
      }

      if (this.payslip.isEnableSocso && this.payslip.employeeSocsoAmount) {
        total += parseFloat(this.payslip.employeeSocsoAmount) || 0;
      }

      if (this.payslip.isEnableEis && this.payslip.employeeEisAmount) {
        total += parseFloat(this.payslip.employeeEisAmount) || 0;
      }

      if (this.payslip.isEnableSkbbk && this.payslip.employeeSkbbkAmount) {
        total += parseFloat(this.payslip.employeeSkbbkAmount) || 0;
      }

      if (this.payslip.isEnableTax && this.payslip.taxAmount) {
        total += parseFloat(this.payslip.taxAmount) || 0;
      }

      if (this.payslip.isEnableZakat && this.payslip.zakatAmount) {
        total += parseFloat(this.payslip.zakatAmount) || 0;
      }

      return total;
    },
    netPay() {
      return this.grossIncome - this.totalDeductions;
    },
    hasStatutoryContributions() {
      return this.payslip.isEnableEpf || this.payslip.isEnableSocso || this.payslip.isEnableEis || this.payslip.isEnableSkbbk;
    },
    hasYTDData() {
      return this.payslip.ytdGrossAmount > 0 || this.payslip.ytdNetPay > 0;
    },
    hasLeaveBalance() {
      return (this.payslip.annualLeaveBalance !== undefined && this.payslip.annualLeaveBalance !== null) ||
             (this.payslip.medicalLeaveBalance !== undefined && this.payslip.medicalLeaveBalance !== null) ||
             (this.payslip.sickLeaveBalance !== undefined && this.payslip.sickLeaveBalance !== null);
    },
    hasYTDOrLeaveData() {
      return this.hasYTDData || this.hasLeaveBalance;
    },
    hasAnyStatutoryDeductions() {
      return (this.payslip.isEnableEpf && this.payslip.employeeEpfAmount > 0) ||
             (this.payslip.isEnableSocso && this.payslip.employeeSocsoAmount > 0) ||
             (this.payslip.isEnableEis && this.payslip.employeeEisAmount > 0) ||
             (this.payslip.isEnableSkbbk && this.payslip.employeeSkbbkAmount > 0) ||
             (this.payslip.isEnableTax && this.payslip.taxAmount > 0) ||
             (this.payslip.isEnableZakat && this.payslip.zakatAmount > 0);
    },
    totalEmployerContributions() {
      if (!this.payslip.employerContributions) return 0;
      return this.payslip.employerContributions.reduce((sum, item) => {
        return sum + (parseFloat(item.amount) || 0);
      }, 0);
    },
    statutorySummary() {
      const summary = [
        { type: 'EPF', typeValue: 0, employeeAmount: 0, employerAmount: 0, totalAmount: 0 },
        { type: 'SOCSO', typeValue: 1, employeeAmount: 0, employerAmount: 0, totalAmount: 0 },
        { type: 'EIS', typeValue: 2, employeeAmount: 0, employerAmount: 0, totalAmount: 0 },
      ];

      // Sum employee deductions by type
      if (this.payslip.deductions) {
        this.payslip.deductions.forEach((deduction) => {
          const summaryItem = summary.find((s) => s.typeValue === deduction.type);
          if (summaryItem) {
            summaryItem.employeeAmount += (parseFloat(deduction.amount) || 0);
          }
        });
      }

      // Sum employer contributions by type
      if (this.payslip.employerContributions) {
        this.payslip.employerContributions.forEach((contribution) => {
          const summaryItem = summary.find((s) => s.typeValue === contribution.type);
          if (summaryItem) {
            summaryItem.employerAmount += (parseFloat(contribution.amount) || 0);
          }
        });
      }

      // Calculate totals
      summary.forEach((item) => {
        item.totalAmount = item.employeeAmount + item.employerAmount;
      });

      return summary;
    },
    totalStatutoryEmployee() {
      return this.statutorySummary.reduce((sum, item) => sum + item.employeeAmount, 0);
    },
    totalStatutoryEmployer() {
      return this.statutorySummary.reduce((sum, item) => sum + item.employerAmount, 0);
    },
    totalStatutory() {
      return this.totalStatutoryEmployee + this.totalStatutoryEmployer;
    },
    isMobile() {
      return /Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    },
  },
  mounted() {
    this.forceA4Size();
  },
  methods: {
    // Amount columns are labelled "(RM)", so cells show the number only.
    formatCurrency(amount) {
      return formatMyr(amount);
    },
    humanize(name) {
      if (!name) return "";
      const words = String(name)
        .replace(/([a-z])([A-Z])/g, "$1 $2")
        .toLowerCase()
        .replace(/\b(epf|socso|eis|pcb|skbbk)\b/g, (w) => w.toUpperCase());
      return words.charAt(0).toUpperCase() + words.slice(1);
    },

    getCurrencyName() {
      const currency = this.payslip.business?.currency || 'MYR';
      const currencyNames = {
        'MYR': 'Malaysian Ringgit',
        'SGD': 'Singapore Dollar',
        'USD': 'US Dollar',
        'EUR': 'Euro',
        'GBP': 'British Pound',
      };
      return currencyNames[currency] || currency;
    },

    getEmployeeName() {
      return this.payslip.profile?.fullName || this.payslip.employeeName || '—';
    },

    getPayrollDate() {
      return this.payslip.date ? formatMytDate(this.payslip.date) : "";
    },

    getPayPeriod() {
      const start = this.payslip.payPeriodStart ? formatMytDate(this.payslip.payPeriodStart) : "";
      const end = this.payslip.payPeriodEnd ? formatMytDate(this.payslip.payPeriodEnd) : "";

      if (start && end) {
        return `${start} – ${end}`;
      }
      return "—";
    },

    formatDate(date) {
      return date ? formatMytDate(date) : "—";
    },

    formatLeave(days) {
      const numDays = parseFloat(days) || 0;
      const text = Number.isInteger(numDays) ? String(numDays) : numDays.toFixed(1);
      return `${text} ${numDays === 1 ? "day" : "days"}`;
    },

    getPaymentMethodName(method) {
      const methods = {
        0: 'Bank Transfer',
        1: 'Cheque',
        2: 'Cash',
        3: 'Other'
      };
      return methods[method] || 'Bank Transfer';
    },

    removeTrailingSlash(str) {
      return str && str.endsWith("/") ? str.slice(0, -1) : str;
    },

    getImageUrl() {
      try {
        if (this.payslip.business?.logoUrl) {
          return this.removeTrailingSlash(apiUrl) + this.payslip.business.logoUrl;
        }
        return "";
      } catch (error) {
        return "";
      }
    },

    formatAddress(address) {
      return address ? address.replace(/\n/g, "<br />") : "";
    },

    forceA4Size() {
      const pdfContent = this.$refs.pdfContent;
      if (pdfContent) {
        pdfContent.style.width = "210mm";
        pdfContent.style.minHeight = "297mm";
      }
    },

    printContent() {
      // Mobile users get PDF download instead
      if (this.isMobile) {
        this.exportPDF();
        return;
      }

      // Desktop users get new window print
      this.openPrintWindow();
    },

    openPrintWindow() {
      const printWindow = window.open('', '_blank', 'width=800,height=600');
      const printContent = this.$refs.pdfContent.innerHTML;

      const printStyles = this.getPrintStyles();

      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
          <head>
            <title>${this.payslip.business?.shortName || 'Payslip'}_${this.payslip.payrollNo || 'PAY'}</title>
            <meta charset="utf-8">
            <meta name="viewport" content="width=device-width, initial-scale=1">
            <style>
              ${printStyles}
            </style>
          </head>
          <body>
            <div class="pdf-content">
              ${printContent}
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.print();
          printWindow.close();
        }, 250);
      };
    },

    async exportPDF() {
      this.isGeneratingPDF = true;

      try {
        const element = this.$refs.pdfContent.cloneNode(true);
        const noPrintElements = element.querySelectorAll('.no-print');
        noPrintElements.forEach(el => el.remove());

        // Apply PDF-specific styling - compact layout
        element.style.width = '794px';
        element.style.minHeight = 'auto';
        element.style.padding = '12px';
        element.style.fontSize = '10px';
        element.style.lineHeight = '1.2';
        element.style.fontFamily = 'Arial, sans-serif';
        element.style.backgroundColor = '#ffffff';

        element.classList.add('pdf-generation');

        // Fix logo - more compact
        const logo = element.querySelector('.company-logo');
        if (logo) {
          logo.style.maxWidth = '120px';
          logo.style.height = 'auto';
          logo.style.display = 'block';
        }

        // Fix tables - more compact
        const tables = element.querySelectorAll('.payroll-table, .statutory-table');
        tables.forEach(table => {
          table.style.width = '100%';
          table.style.fontSize = '9px';
          table.style.minWidth = 'auto';
          table.style.marginBottom = '6px';
        });

        const tableCells = element.querySelectorAll('th, td');
        tableCells.forEach(cell => {
          cell.style.padding = '3px 5px';
          cell.style.fontSize = '9px';
          cell.style.whiteSpace = 'normal';
          cell.style.lineHeight = '1.2';
        });

        // Fix typography - more compact
        const h1 = element.querySelector('.document-title');
        if (h1) {
          h1.style.fontSize = '20px';
          h1.style.margin = '0 0 5px 0';
        }

        const h5 = element.querySelector('.company-name');
        if (h5) {
          h5.style.fontSize = '12px';
          h5.style.margin = '0 0 3px 0';
        }

        const h6Elements = element.querySelectorAll('.section-title');
        h6Elements.forEach(h6 => {
          h6.style.fontSize = '10px';
          h6.style.margin = '0 0 3px 0';
        });

        // Fix layout
        const rows = element.querySelectorAll('.row');
        rows.forEach(row => {
          row.style.display = 'flex';
          row.style.flexWrap = 'wrap';
          row.style.margin = '0 -7.5px';
        });

        const cols = element.querySelectorAll('.col');
        cols.forEach(col => {
          col.style.flex = '1';
          col.style.padding = '0 7.5px';
        });

        const colMd6 = element.querySelectorAll('.col-md-6');
        colMd6.forEach(col => {
          col.style.flex = '0 0 50%';
          col.style.maxWidth = '50%';
          col.style.padding = '0 7.5px';
        });

        const offsetMd6 = element.querySelectorAll('.offset-md-6');
        offsetMd6.forEach(col => {
          col.style.marginLeft = '50%';
        });

        // Fix alignment
        const textRight = element.querySelectorAll('.text-right');
        textRight.forEach(el => {
          el.style.textAlign = 'right';
        });

        const textCenter = element.querySelectorAll('.text-center');
        textCenter.forEach(el => {
          el.style.textAlign = 'center';
        });

        // Fix footer
        const footer = element.querySelector('.document-footer');
        if (footer) {
          footer.style.display = 'block';
          footer.style.textAlign = 'center';
          footer.style.marginTop = '30px';
          footer.style.paddingTop = '15px';
          footer.style.borderTop = '1px solid #ccc';
          footer.style.pageBreakInside = 'avoid';
        }

        const footerLogo = element.querySelector('.footer-logo');
        if (footerLogo) {
          footerLogo.style.maxWidth = '20px';
          footerLogo.style.height = 'auto';
          footerLogo.style.verticalAlign = 'middle';
          footerLogo.style.filter = 'grayscale(100%)';
        }

        // Generate filename
        const employeeName = this.getEmployeeName().replace(/[^a-zA-Z0-9]/g, '_');
        const filename = `${this.payslip.business?.shortName || 'Payslip'}_${employeeName}_${this.payslip.payrollNo || 'PAY'}.pdf`;

        const opt = {
          margin: [8, 8, 8, 8],
          filename: filename,
          image: { type: 'jpeg', quality: 0.95 },
          html2canvas: {
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: '#ffffff',
            scrollX: 0,
            scrollY: 0
          },
          jsPDF: {
            unit: 'mm',
            format: 'a4',
            orientation: 'portrait',
            compress: true
          },
          pagebreak: { mode: ['avoid-all', 'css', 'legacy'], avoid: '.payroll-table, .statutory-table, .net-pay-box' }
        };

        await html2pdf().set(opt).from(element).save();
      } catch (error) {
        console.error('PDF generation failed:', error);
        this.openPrintWindow();
      } finally {
        this.isGeneratingPDF = false;
      }
    },

    getPrintStyles() {
      return `
        * {
          box-sizing: border-box;
        }

        body {
          margin: 0;
          padding: 0;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          background: white;
        }

        .pdf-content {
          width: 794px;
          min-height: 1123px;
          margin: 0 auto;
          padding: 20px;
          background-color: white;
          font-size: 14px;
          line-height: 1.4;
          page-break-inside: avoid;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .no-print {
          display: none !important;
        }

        .company-logo {
          max-width: 180px;
          height: auto;
          display: block;
        }

        .remarks-box {
          background-color: #fff3cd;
          border: 1px solid #ffc107;
          border-radius: 0.25rem;
        }

        .remarks-text {
          color: #856404;
          font-size: 10px;
          line-height: 1.4;
        }

        .row {
          display: flex;
          flex-wrap: wrap;
          margin: 0 -7.5px;
        }

        .col {
          flex: 1;
          padding: 0 7.5px;
        }

        .col-md-6 {
          flex: 0 0 50%;
          max-width: 50%;
          padding: 0 7.5px;
        }

        .offset-md-6 {
          margin-left: 50%;
        }

        .text-center { text-align: center; }
        .text-right { text-align: right; }

        .section-divider {
          border: 2px solid #2c3e50;
          margin: 10px 0;
        }

        .payroll-table, .statutory-table {
          width: 100%;
          border-collapse: collapse;
          margin: 15px 0;
          page-break-inside: auto;
        }

        .payroll-table th,
        .payroll-table td,
        .statutory-table th,
        .statutory-table td {
          border: 1px solid #bdc3c7;
          padding: 8px 12px;
          text-align: left;
          vertical-align: top;
          font-size: 13px;
        }

        .payroll-table th,
        .statutory-table th {
          background-color: #34495e;
          color: white;
          font-weight: bold;
        }

        .table-total, .table-grand-total {
          background-color: #ecf0f1;
          font-weight: bold;
        }

        .net-pay-box {
          background-color: #d4edda;
          border: 2px solid #28a745;
          border-radius: 0.25rem;
        }

        .document-footer {
          text-align: center;
          margin-top: 20px;
          padding-top: 15px;
          border-top: 1px solid #ccc;
          font-size: 10px;
          page-break-inside: avoid;
        }

        .footer-logo {
          max-width: 18px;
          height: auto;
          vertical-align: middle;
        }

        @media print {
          .pdf-content {
            width: 100%;
            margin: 0;
            padding: 15mm;
          }
        }
      `;
    },
  },
};
</script>

<style scoped>
.pdf-content {
  width: 210mm;
  min-height: 297mm;
  max-width: 100%;
  margin: 0 auto;
  padding: 12mm;
  box-sizing: border-box;
  background-color: white;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  line-height: 1.3;
  color: #333;
  font-size: 0.9rem;
}

.document-header {
  margin-bottom: 0.75rem;
}

.company-logo {
  max-width: 160px;
  height: auto;
}

.document-title {
  font-size: 1.75rem;
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 0.25rem;
}

.company-name {
  font-size: 1rem;
  font-weight: 600;
  color: #34495e;
}

.company-reg, .company-address, .company-phone {
  font-size: 0.75rem;
  color: #7f8c8d;
  margin-bottom: 0.1rem;
}

.section-divider {
  border: 1.5px solid #2c3e50;
  margin: 0.5rem 0;
}

.document-details {
  margin-bottom: 0.75rem;
}

.section-title {
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.3rem;
  font-size: 0.9rem;
}

.info-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0 0.25rem;
}

.info-label {
  font-weight: 600;
  color: #2c3e50;
  padding-right: 1rem;
  white-space: nowrap;
  vertical-align: top;
  width: 40%;
}

.info-value {
  color: #5a6c7d;
  vertical-align: top;
}

.payroll-table, .statutory-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
}

.payroll-table th,
.statutory-table th {
  background-color: #34495e;
  color: white;
  font-weight: 600;
  padding: 0.4rem 0.4rem;
  border: 1px solid #2c3e50;
  font-size: 0.85rem;
}

.payroll-table td,
.statutory-table td {
  padding: 0.35rem 0.4rem;
  border: 1px solid #bdc3c7;
  vertical-align: top;
  font-size: 0.85rem;
}

.payroll-table tbody tr:nth-child(even) {
  background-color: #f8f9fa;
}

.table-total {
  background-color: #ecf0f1 !important;
  font-weight: 600;
}

.table-grand-total {
  background-color: #d5dbdb !important;
  font-size: 0.95rem;
  font-weight: 600;
}

.net-pay-box {
  background-color: #d4edda;
  border: 2px solid #28a745;
  border-radius: 0.25rem;
}

.remarks-box {
  background-color: #fff3cd;
  border: 1px solid #ffc107;
  border-radius: 0.25rem;
}

.remarks-text {
  color: #856404;
  font-size: 0.85rem;
  line-height: 1.4;
  white-space: pre-wrap;
}

.document-footer {
  margin-top: 1rem;
  padding-top: 0.75rem;
}

.footer-divider {
  border-top: 1px solid #bdc3c7;
  margin-bottom: 0.5rem;
}

.company-info {
  font-weight: 600;
  color: #2c3e50;
  font-size: 0.75rem;
}

.footer-note {
  color: #6c757d;
  font-size: 0.7rem;
  font-style: italic;
}

.powered-by {
  color: #7f8c8d;
  font-size: 0.7rem;
}

.footer-logo {
  max-width: 18px;
  height: auto;
  filter: grayscale(100%);
  margin: 0 0.2rem;
  vertical-align: middle;
}

@media print {
  .no-print {
    display: none !important;
  }

  .pdf-content {
    width: 100%;
    min-height: auto;
    margin: 0;
    padding: 10mm;
    font-size: 10px;
    line-height: 1.2;
  }

  .document-title {
    font-size: 20px;
  }

  .section-title {
    font-size: 11px;
  }

  .payroll-table, .statutory-table {
    font-size: 9px;
  }

  .payroll-table th, .payroll-table td,
  .statutory-table th, .statutory-table td {
    padding: 3px 4px;
    font-size: 9px;
  }
}

@media screen and (max-width: 768px) {
  .pdf-content {
    width: 100%;
    padding: 15px;
    min-height: auto;
  }

  .document-title {
    font-size: 1.8rem;
  }

  .company-logo {
    max-width: 150px;
  }
}
</style>
